<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { SPOT_SIZES, usePrivacy } from '@/stores/privacy.js'
import UiMenu from '@/components/ui/UiMenu.vue'

/** The eye in the top bar (D-260): privacy off, discreet or full, and the screen covered at once. */
const privacy = usePrivacy()
const { t } = useI18n()
const items = computed(() => [
  { header: true, label: `${t('privacy.title')} · Ctrl+Shift+P` },
  ...[null, 'discreet', 'discreet_spot', 'full', 'spot'].map((mode) => ({
    label: t(`privacy.modes.${mode || 'off'}`),
    title: mode ? t(`privacy.hints.${mode}`) : undefined,
    icon: { null: 'fa-eye', discreet: 'fa-eye-low-vision', discreet_spot: 'fa-glasses', full: 'fa-eye-slash', spot: 'fa-magnifying-glass' }[String(mode)],
    hint: privacy.mode === mode ? '✓' : '',
    testid: `privacy-${mode || 'off'}`,
    action: () => privacy.set(mode),
  })),
  ...(['spot', 'discreet_spot'].includes(privacy.mode) ? [
    { label: t('privacy.spotSmaller'), hint: `${privacy.spotSize + 1}/${SPOT_SIZES.length}`, icon: 'fa-magnifying-glass-minus', testid: 'privacy-spot-smaller', disabled: privacy.spotSize === 0, keep: true, action: () => privacy.resizeSpot(-1) },
    { label: t('privacy.spotBigger'), hint: 'Alt + ⇅', icon: 'fa-magnifying-glass-plus', testid: 'privacy-spot-bigger', disabled: privacy.spotSize === SPOT_SIZES.length - 1, keep: true, action: () => privacy.resizeSpot(1) },
  ] : []),
  { divider: true },
  { label: t('privacy.coverNow'), hint: 'Esc Esc', icon: 'fa-eye-slash', testid: 'privacy-cover-now', disabled: !privacy.on, action: () => privacy.cover() },
])
</script>

<template>
  <UiMenu
    :items="items"
    :icon="privacy.on ? 'fa-eye-slash' : 'fa-eye'"
    :button-class="['inline-flex h-9 w-9 items-center justify-center rounded-lg', privacy.on ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-100' : 'text-muted hover:bg-[var(--surface-3)]'].join(' ')"
    variant="none"
    :label="''"
    :aria-label="`${$t('privacy.title')}: ${$t(`privacy.modes.${privacy.mode || 'off'}`)} (Ctrl+Shift+P)`"
    :title="`${$t('privacy.title')} (Ctrl+Shift+P)`"
    data-testid="privacy-toggle"
    :list-attrs="{ 'data-privacy-public': '', 'data-testid': 'privacy-menu' }"
  />
</template>
